import React from 'react';
import { Package, CheckCircle2, Truck, ShieldCheck } from 'lucide-react';
import { StatCard } from '../../../../../components/ui/StatCard';
import {
  buildRateTrend,
  buildTrend,
  formatNumber,
  formatPercent,
} from '../../../../../features/dashboard/dashboardUtils';

const SKELETON = 'h-[148px] rounded-2xl bg-ninja-gray dark:bg-[#1a1a1a] animate-pulse';

/**
 * The four numbers at the top.
 *
 * Every one of them is computed on the backend, from the status groupings in
 * `DeliveryStatusEnum` — this only formats. Three carry a comparison against
 * the previous window of the same length; the comparison disappears when there
 * is nothing to compare against, since "↑ 100%" over an empty week is noise.
 *
 * @param {object} props
 * @param {object|null} props.kpis
 * @param {object|null} props.previous
 * @param {boolean} props.isLoading
 */
export const DashboardKpis = ({ kpis, previous, isLoading }) => {
  if (isLoading && !kpis) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {[0, 1, 2, 3].map((i) => <div key={i} className={SKELETON} />)}
      </div>
    );
  }

  const totals = kpis || {};
  const before = previous || {};

  const totalTrend = buildTrend(totals.total, before.total, 'vs periodo anterior');
  const deliveredTrend = buildTrend(totals.delivered, before.delivered, 'vs periodo anterior');
  // In points, not in percent: this one is already a rate. See `buildRateTrend`.
  const successTrend = buildRateTrend(totals.success_rate, before.success_rate, 'vs periodo anterior');

  return (
    <div data-testid="dashboard-kpis" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
      <StatCard
        title="Total Deliveries"
        value={formatNumber(totals.total)}
        icon={Package}
        tone="info"
        subtitle={totalTrend ? undefined : 'Sin periodo anterior con que comparar'}
        {...(totalTrend || {})}
      />

      <StatCard
        title="Entregados"
        value={formatNumber(totals.delivered)}
        icon={CheckCircle2}
        tone="success"
        subtitle={`${formatPercent(totals.delivery_rate)} tasa de entrega`}
        {...(deliveredTrend || {})}
      />

      <StatCard
        title="En Proceso"
        value={formatNumber(totals.in_process)}
        icon={Truck}
        tone="warning"
        subtitle="Todo lo que no está entregado ni cerrado con problema"
      />

      <StatCard
        title="Tasa Éxito"
        value={formatPercent(totals.success_rate)}
        icon={ShieldCheck}
        tone={totals.failed || totals.returning ? "danger" : "success"}
        subtitle={`${formatNumber(totals.failed)} fallidos • ${formatNumber(totals.returning)} en devolución`}
        {...(successTrend || {})}
      />
    </div>
  );
};
